// ==================== Container Monitor Types ====================

export type MonitorStatus = 'active' | 'paused' | 'stopped';
export type ContainerStatus = 'on-vessel' | 'on-hold' | 'released' | 'picked-up';
export type LfdAlertLevel = 'normal' | 'warning' | 'critical' | 'overdue';
/**
 * Every category a parser can emit. Beyond literal holds this covers the
 * non-hold answers to "why can't I pick it up" — a container still on the
 * ship or moved off-dock used to be reported as `inconsistent`.
 * Kept in sync with backend/src/services/extension/parsers/holdDetails.js.
 */
export type HoldType =
  | 'customs' | 'carrier' | 'terminal' | 'cbpa' | 'ctf' | 'tmf'
  | 'freight' | 'demurrage' | 'exam'
  // `gate-fee` / `closed-area` 后端一直在发，这个联合类型漏了它们（2026-10-08 补）。
  // `in-processing` 是同一天新加的一类：码头正在挪柜子，原先读成 `inconsistent`。
  | 'gate-fee' | 'closed-area' | 'in-processing'
  | 'not-at-terminal' | 'on-vessel' | 'inconsistent';
export type TerminalSite = 'oict' | 'ets' | 'trapac';

/**
 * The eight milestones PCC Logistics publishes for a container sitting at the
 * customs exam station. ISO dates, or null where PCC has not filled that row in.
 *
 * Deliberately NOT part of `TerminalSite`: PCC is not a terminal, and a
 * container has a terminal AND (possibly) a PCC record at the same time. The
 * Container Monitor shows them side by side for that reason.
 */
export interface PccDates {
  arrival: string | null;
  lfd: string | null;
  stagedForExam: string | null;
  go: string | null;
  freightRelease: string | null;
  perDiem: string | null;
  equipmentRelease: string | null;
  pickUp: string | null;
}

/** One lookup's worth of what PCC's availability page said about a container. */
export interface PccRecord {
  dockReceipt: string | null;
  hbl: string | null;
  mbl: string | null;
  cfsRef: string | null;
  status: string | null;
  outSeal: string | null;
  availableMessage: string | null;
  charges: string | null;
  pickupBy: string | null;
  pickupNumber: string | null;
  totalPieces: string | null;
  weight: string | null;
  measure: string | null;
  pallets: { shipper: string | null; exchange: string | null; total: string | null };
  dates: PccDates;
  /**
   * Which milestones PCC has marked done. Taken from the page's green
   * highlight, not from "the date is non-empty" — the page's own legend says
   * green means completed.
   */
  completed: (keyof PccDates)[];
  checkedAt: string;
}

/** Order the dates are shown in — the order the container actually walks them. */
export const PCC_DATE_ORDER: { key: keyof PccDates; label: string; short: string }[] = [
  { key: 'arrival', label: 'Arrival at PCC', short: 'Arr' },
  { key: 'lfd', label: 'Last Free Day', short: 'LFD' },
  { key: 'stagedForExam', label: 'Staged for Exam', short: 'Exam' },
  { key: 'go', label: 'Gov Order (G/O)', short: 'G/O' },
  { key: 'freightRelease', label: 'Freight Release (D/O)', short: 'D/O' },
  { key: 'perDiem', label: 'Per Diem starts', short: 'PerD' },
  { key: 'equipmentRelease', label: 'Equipment Release', short: 'EIR' },
  { key: 'pickUp', label: 'Picked Up', short: 'PU' },
];

/**
 * The answer to one on-demand PCC lookup (the PCC refresh button).
 *
 * `found` and `arrived` are deliberately two fields: PCC opens a file for a
 * shipment long before the box is there, so "PCC knows this container" and
 * "the container is at the exam station" are different answers and the panel
 * has to be able to say which one it got.
 *
 * `message` is the server's own sentence for this outcome — carried inside the
 * payload because api.ts keeps only `data` on a success response.
 */
export interface PccCheckResult {
  ok: true;
  containerNumber: string;
  found: boolean;
  arrived: boolean;
  /** This check is what pushed the task to CBP Hold. */
  confirmed: boolean;
  /** Why PCC had nothing, when it had nothing ('no_records', ...). */
  reason: string | null;
  message: string;
}

/**
 * One reason a container is blocked. A container commonly carries several.
 * `label` is display-ready; `detail` is the terminal's own wording, shown on
 * hover so a dispatcher can see exactly what the terminal said.
 */
export interface ContainerHoldDetail {
  category: HoldType;
  code: string | null;
  label: string;
  detail: string | null;
}

export interface ContainerMonitor {
  id: number;
  container_number: string;
  order_id: string;
  task_id: number | null;
  terminal_site: TerminalSite | null;
  terminal_site_confirmed: boolean;
  monitor_status: MonitorStatus;
  stop_reason: string | null;
  latest_status: ContainerStatus | null;
  latest_holds: HoldType[];
  latest_hold_details: ContainerHoldDetail[] | null;
  latest_terminal_hold_codes: string | null;
  latest_lfd: string | null;
  latest_lfd_good_through: string | null;
  latest_fees: number;
  latest_vessel: string | null;
  latest_voyage: string | null;
  latest_location: string | null;
  latest_yard_status: string | null;
  latest_shipping_line: string | null;
  latest_container_size: string | null;
  lfd_alert_level: LfdAlertLevel;
  demurrage_days: number;
  last_checked_at: string | null;
  last_changed_at: string | null;
  check_count: number;
  /**
   * How many checks in a row the terminal answered and had NO record of this
   * container. Resets to 0 on any successful read.
   *
   * > 0 together with `latest_status === null` is the difference between "the
   * terminal told us it has never heard of this box" and "nobody has answered
   * us yet" — two facts the row used to render identically as Unknown.
   */
  not_found_count: number;
  /**
   * How many checks in a row came back with ONLY a previous-voyage record for
   * this number. Also resets on a successful read. Same role as above: it is
   * why the row is blank, and it means we are probably watching the wrong
   * terminal rather than that nothing is known.
   */
  residue_only_count: number;
  next_check_at: string | null;
  created_at: string;
  updated_at: string;
  /**
   * PCC's availability page, last time we read it. Only ever populated for
   * containers in the "Transfer for Exam · PCC Picked" group.
   */
  pcc_data: PccRecord | null;
  /**
   * When PCC was first found to have this shipment on file. Non-null is what
   * pushed the task to CBP Hold.
   *
   * Since 2026-09-24 a record alone is enough — the eight dates are shown as
   * progress, not used as the gate. Rows written before that only got this
   * timestamp once a date appeared.
   */
  pcc_found_at: string | null;
  pcc_last_checked_at: string | null;
  pcc_next_check_at: string | null;
  pcc_check_count: number;
  // Joined from orders table
  import_export?: string;
  order_type?: string;
  /**
   * 谁的柜子 —— 就是订单的 Ord. Party，和派车板那列同源同值，不做任何回退。
   * 订单没填下单方、或这只柜子在本租户匹配不到订单时为 null。
   */
  client_name?: string | null;
}

export interface StatusChangeLog {
  id: number;
  monitor_id: number;
  container_number: string;
  terminal_site: string | null;
  old_status: ContainerStatus | null;
  new_status: ContainerStatus | null;
  old_holds: HoldType[] | null;
  new_holds: HoldType[] | null;
  new_hold_details: ContainerHoldDetail[] | null;
  old_lfd: string | null;
  new_lfd: string | null;
  raw_data: Record<string, unknown> | null;
  source: string;
  checked_at: string;
  order_id?: string;
}

export interface MonitorStats {
  active_count: string;
  released_count: string;
  on_hold_count: string;
  on_vessel_count: string;
  /** Active monitors that have never come back with a status (latest_status IS NULL). */
  unknown_count: string;
  urgent_count: string;
  warning_count: string;
  stopped_count: string;
}

export interface MonitorStatusChangeEvent {
  monitorId: number;
  containerNumber: string;
  orderId: string;
  oldStatus: ContainerStatus;
  newStatus: ContainerStatus;
  terminalSite: string;
  holds: HoldType[];
  holdDetails: ContainerHoldDetail[];
  lfd: string | null;
  lfdAlertLevel: LfdAlertLevel;
}

export interface MonitorLfdChangeEvent {
  monitorId: number;
  containerNumber: string;
  orderId: string;
  oldLfd: string | null;
  newLfd: string | null;
  alertLevel: LfdAlertLevel;
}

export interface MonitorAlertEvent {
  monitorId: number;
  containerNumber: string;
  alertType: string;
  message: string;
}
